import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import AnalyticsKpiCard from '../components/AnalyticsKpiCard';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  Clock,
  Pulse,
  Robot,
  TrendUp,
  WarningCircle
} from '@phosphor-icons/react';
const AnalyticsDashboard = () => {
  const { activeOrganization } = useOrganization();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [trendsData, setTrendsData] = useState([]);

  const loadAnalytics = useCallback(async (isRefresh = false) => {
    // Don't load if no organization context
    if (!activeOrganization?.organization_id) {
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch dashboard metrics
      const dashboardRes = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/dashboard?days=${dateRange}`,
        {},
        activeOrganization.organization_id
      );

      if (!dashboardRes.ok) {
        const errorData = await dashboardRes.json().catch(() => ({}));
        // Parse error detail properly
        let errorMessage = 'Failed to load dashboard metrics';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = JSON.stringify(errorData.detail);
          }
        }
        throw new Error(errorMessage);
      }

      const dashboardJson = await dashboardRes.json();
      setDashboardData(dashboardJson);

      // Fetch trends data
      const trendsRes = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/trends?days=${dateRange}&grouping=day`,
        {},
        activeOrganization.organization_id
      );

      if (trendsRes.ok) {
        const trendsJson = await trendsRes.json();
        setTrendsData(trendsJson.trends || []);
      }

    } catch (err) {
      console.error('Error loading analytics:', err);
      // Ensure error is always a string
      let errorMessage = 'Failed to load analytics data';
      if (err.message && typeof err.message === 'string') {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeOrganization, dateRange]);

  useEffect(() => {
    if (activeOrganization) {
      loadAnalytics();
    }
  }, [activeOrganization, loadAnalytics]);

  const handleRefresh = () => {
    loadAnalytics(true);
  };

  const formatNumber = (value) => {
    return value.toLocaleString();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header */}
        {true && (
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <TrendUp size={32} className="text-[#6366F1]" />
                <h1 className="text-4xl sm:text-5xl font-bold text-white">Analytics Dashboard</h1>
              </div>
              <p className="text-[#A0AEC0] text-base">
                Monitor your AI agent usage, costs, and performance
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Range Selector */}
              <div className="flex gap-2">
                {[7, 30, 90].map((days) => (
                  <button
                    key={days}
                    onClick={() => setDateRange(days)}
                    className={`px-4 py-2 rounded-full font-semibold transition-all ${
                      dateRange === days
                        ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
                        : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>

              {/* ArrowsClockwise Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-all disabled:opacity-50"
                title="Refresh data"
              >
                <ArrowsClockwise size={20} className={`text-[#6366F1] ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        )}

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-6 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3"
              >
                <WarningCircle size={20} className="text-[#EF4444]" />
                <p className="text-[#EF4444] text-sm">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* KPI Cards */}
          {dashboardData && (
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <AnalyticsKpiCard
                icon={Pulse}
                label="Total Calls"
                value={formatNumber(dashboardData.total_calls || 0)}
                trend={dashboardData.trends?.calls_change_percent}
              />
              <AnalyticsKpiCard
                icon={CheckCircle}
                label="Success Rate"
                value={dashboardData.success_rate || 0}
                unit="%"
              />
              <AnalyticsKpiCard
                icon={Clock}
                label="Avg Response"
                value={dashboardData.avg_response_time_ms || 0}
                unit="ms"
              />
            </div>
          )}

          {/* Usage Trend Chart */}
          {trendsData.length > 0 && (
            <div className="glass rounded-2xl p-6 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <TrendUp size={24} className="text-[#6366F1]" />
                Usage Trends
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendsData}>
                  <defs>
                    <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    stroke="#A0AEC0"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis
                    stroke="#6366F1"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.95)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      padding: '12px'
                    }}
                    labelStyle={{ color: '#A0AEC0', marginBottom: '8px' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                  />
                  <Area
                    type="monotone"
                    dataKey="calls"
                    stroke="#6366F1"
                    fillOpacity={1}
                    fill="url(#colorCalls)"
                    name="API Calls"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid lg:grid-cols-1 gap-8 mb-8">
            {/* Top Agents Table */}
            {dashboardData?.top_agents && dashboardData.top_agents.length > 0 && (
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Robot size={24} className="text-[#6366F1]" />
                  Top Agents
                </h2>
                <div className="space-y-3">
                  {dashboardData.top_agents.slice(0, 5).map((agent, idx) => (
                    <div
                      key={agent.agent_id}
                      className="flex items-center justify-between p-4 rounded-xl glass hover:bg-[rgba(255,255,255,0.08)] transition-all cursor-pointer"
                      onClick={() => navigate(`/analytics/agents/${agent.agent_id}`)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center text-white font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-semibold">{agent.agent_name || 'Unknown Agent'}</h4>
                          <p className="text-[#A0AEC0] text-sm">
                            {formatNumber(agent.calls)} calls
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#14F195] text-sm font-semibold">
                          {agent.success_rate}% success
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Empty State */}
          {dashboardData && dashboardData.total_calls === 0 && (
            <div className="glass rounded-2xl p-12 text-center">
              <Pulse size={64} className="mx-auto mb-6 text-[#A0AEC0]" />
              <h2 className="text-2xl font-bold text-white mb-4">No Analytics Data Yet</h2>
              <p className="text-[#A0AEC0] mb-8">
                Start using your AI agents to see analytics and performance metrics here.
              </p>
              <button
                onClick={() => navigate('/partner-pro/ai-workforce/agents')}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold hover:opacity-90 transition-opacity"
              >
                View Your Agents
              </button>
            </div>
          )}
        </div>
  );
};

export default AnalyticsDashboard;
